import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import HLSVideoPlayer from '../components/HLSVideoPlayer';
import StreamGifts from '../components/StreamGifts';
import apiService from '../services/api';

/**
 * Live Streaming Page
 * Supports watching live streams, sending gifts, and chat interaction
 */

const GIFT_TYPES = [
  { id: 'heart', emoji: '❤️', name: 'Heart', coins: 10 },
  { id: 'star', emoji: '⭐', name: 'Star', coins: 50 },
  { id: 'fire', emoji: '🔥', name: 'Fire', coins: 100 },
  { id: 'diamond', emoji: '💎', name: 'Diamond', coins: 500 },
  { id: 'crown', emoji: '👑', name: 'Crown', coins: 1000 },
  { id: 'rocket', emoji: '🚀', name: 'Rocket', coins: 5000 },
];

export default function LiveStreamPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const playerRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Stream state
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(true);

  // Gift state
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [giftAnimations, setGiftAnimations] = useState([]);
  const [userBalance, setUserBalance] = useState(0);

  // Stats
  const [streamStats, setStreamStats] = useState({
    totalCoins: 0,
    giftCount: 0,
    uniqueGifters: 0,
  });

  // Fetch stream data
  useEffect(() => {
    const fetchStream = async () => {
      if (!streamId) return;
      
      try {
        setLoading(true);
        const response = await apiService.get(`/api/live/${streamId}`);
        setStream(response);
        setViewerCount(response.viewer_count || 0);
        
        // Fetch gift stats
        const stats = await apiService.get(`/api/monetization/gifts/stream/${streamId}/stats`);
        setStreamStats(stats);
      } catch (err) {
        setError(err.message || 'Failed to load stream');
      } finally {
        setLoading(false);
      }
    };

    fetchStream();
  }, [streamId]);

  // Fetch user wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!isAuthenticated) return;
      try {
        const wallet = await apiService.get('/api/wallet/balance');
        setUserBalance(wallet.balance || 0);
      } catch (err) {
        console.error('Failed to fetch wallet:', err);
      }
    };
    fetchBalance();
  }, [isAuthenticated]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Send chat message
  const sendMessage = useCallback(async () => {
    if (!chatInput.trim() || !isAuthenticated) return;

    const newMessage = {
      id: Date.now(),
      user_id: user?.id,
      username: user?.username || 'Anonymous',
      content: chatInput,
      timestamp: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput('');

    // In production, send via WebSocket
    // socket.emit('chat_message', { streamId, content: chatInput });
  }, [chatInput, isAuthenticated, user]);

  // Send gift
  const sendGift = useCallback(async (gift) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (userBalance < gift.coins) {
      alert('Insufficient balance! Please top up your wallet.');
      return;
    }

    try {
      await apiService.post('/api/monetization/gifts', {
        stream_id: parseInt(streamId),
        sender_id: user.id,
        gift_type: gift.id,
        quantity: 1,
        coin_value: gift.coins,
      });

      // Show animation
      const animationId = Date.now();
      setGiftAnimations((prev) => [...prev, { id: animationId, gift }]);
      setTimeout(() => {
        setGiftAnimations((prev) => prev.filter((a) => a.id !== animationId));
      }, 3000);

      // Update balance and stats
      setUserBalance((prev) => prev - gift.coins);
      setStreamStats((prev) => ({
        ...prev,
        totalCoins: prev.totalCoins + gift.coins,
        giftCount: prev.giftCount + 1,
      }));

      setShowGiftPicker(false);
    } catch (err) {
      console.error('Failed to send gift:', err);
      alert('Failed to send gift. Please try again.');
    }
  }, [isAuthenticated, userBalance, streamId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <h2 className="text-xl mb-4">😔 Stream not found</h2>
        <p className="text-gray-400 mb-6">{error || 'This stream may have ended'}</p>
        <button
          onClick={() => navigate('/live')}
          className="px-6 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
        >
          Browse Live Streams
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col lg:flex-row">
      {/* Video Player Section */}
      <div className="flex-1 relative">
        <HLSVideoPlayer
          ref={playerRef}
          src={stream.playback_url}
          autoPlay
          muted={false}
          loop={false}
          className="w-full h-[50vh] lg:h-screen"
        />

        {/* Stream Info Overlay */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          {/* Streamer info */}
          <div className="flex items-center gap-3 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              {stream.creator_username?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <p className="font-semibold">{stream.creator_username || 'Streamer'}</p>
              <p className="text-xs text-gray-300">{stream.title}</p>
            </div>
          </div>

          {/* Live badge & viewers */}
          <div className="flex items-center gap-2">
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
              LIVE
            </span>
            <span className="bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full">
              👁 {viewerCount.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Gift Animations */}
        <AnimatePresence>
          {giftAnimations.map((anim) => (
            <motion.div
              key={anim.id}
              initial={{ opacity: 0, scale: 0.5, y: 100 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 2, y: -100 }}
              transition={{ duration: 0.5 }}
              className="absolute bottom-32 left-1/2 transform -translate-x-1/2 text-8xl"
            >
              {anim.gift.emoji}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Action Bar */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
          {/* Gift button */}
          <button
            onClick={() => setShowGiftPicker(!showGiftPicker)}
            className="bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-3 rounded-full font-semibold flex items-center gap-2 hover:scale-105 transition-transform"
          >
            🎁 Send Gift
          </button>

          {/* Stream stats */}
          <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-sm">
            <span className="text-yellow-400">💰 {streamStats.totalCoins.toLocaleString()}</span>
            <span className="mx-2 text-gray-500">|</span>
            <span>🎁 {streamStats.giftCount}</span>
          </div>

          {/* Chat toggle (mobile) */}
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className="lg:hidden bg-gray-800 p-3 rounded-full"
          >
            💬
          </button>
        </div>

        {/* Gift Picker */}
        <AnimatePresence>
          {showGiftPicker && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="absolute bottom-20 left-4 right-4 bg-gray-900/95 backdrop-blur-sm rounded-xl p-4"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Send a Gift</h3>
                <span className="text-yellow-400 text-sm">
                  Balance: {userBalance.toLocaleString()} coins
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {GIFT_TYPES.map((gift) => (
                  <button
                    key={gift.id}
                    onClick={() => sendGift(gift)}
                    disabled={userBalance < gift.coins}
                    className={`flex flex-col items-center p-3 rounded-lg transition-all ${
                      userBalance >= gift.coins
                        ? 'bg-gray-800 hover:bg-gray-700 hover:scale-105'
                        : 'bg-gray-800/50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <span className="text-3xl mb-1">{gift.emoji}</span>
                    <span className="text-xs">{gift.name}</span>
                    <span className="text-xs text-yellow-400">{gift.coins}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Chat Section */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="w-full lg:w-96 bg-gray-900 flex flex-col h-[50vh] lg:h-screen"
          >
            {/* Chat header */}
            <div className="p-4 border-b border-gray-800 flex justify-between items-center">
              <h3 className="font-semibold">Live Chat</h3>
              <span className="text-xs text-gray-400">
                {chatMessages.length} messages
              </span>
            </div>

            {/* Chat messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-3"
            >
              {chatMessages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No messages yet. Be the first to chat!
                </p>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} className="flex gap-2">
                    <span className="text-purple-400 font-medium text-sm">
                      {msg.username}:
                    </span>
                    <span className="text-gray-200 text-sm">{msg.content}</span>
                  </div>
                ))
              )}
            </div>

            {/* Chat input */}
            <div className="p-4 border-t border-gray-800">
              {isAuthenticated ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!chatInput.trim()}
                    className="bg-purple-600 px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-purple-600 py-2 rounded-lg hover:bg-purple-700"
                >
                  Login to Chat
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Live Streams Browse Page
 */
export function LiveStreamsBrowse() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        const response = await apiService.get('/api/live/streams/active');
        setStreams(response || []);
      } catch (err) {
        console.error('Failed to fetch streams:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStreams();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <h1 className="text-3xl font-bold mb-8">🔴 Live Now</h1>

      {streams.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg mb-4">No live streams right now</p>
          <p className="text-gray-500">Check back later or start your own stream!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {streams.map((stream) => (
            <motion.div
              key={stream.id}
              whileHover={{ scale: 1.02 }}
              className="bg-gray-800 rounded-xl overflow-hidden cursor-pointer"
              onClick={() => navigate(`/live/${stream.id}`)}
            >
              {/* Thumbnail / Preview */}
              <div className="relative aspect-video bg-gray-700">
                {stream.thumbnail_url ? (
                  <img
                    src={stream.thumbnail_url}
                    alt={stream.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl">
                    📺
                  </div>
                )}
                <span className="absolute top-2 left-2 bg-red-600 text-xs font-bold px-2 py-1 rounded animate-pulse">
                  LIVE
                </span>
                <span className="absolute bottom-2 right-2 bg-black/70 text-xs px-2 py-1 rounded">
                  👁 {stream.viewer_count?.toLocaleString() || 0}
                </span>
              </div>

              {/* Stream info */}
              <div className="p-4">
                <h3 className="font-semibold truncate">{stream.title}</h3>
                <p className="text-gray-400 text-sm mt-1">
                  {stream.creator_username || 'Anonymous'}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
